<template>
    <div v-bind="$attrs" class="rounded-xl border border-valence-grey-300 flex justify-between p-5 bg-white">
        <div class="flex flex-col md:flex-row gap-y-2 gap-x-4 justify-center md:justify-start md:items-center grow">
            <p class="body-small font-semibold">After Role Play You'll Get:</p>
            <div v-for="{ iconClass, content, bgColorClass } in previewContent" :key="content" class="flex gap-2 items-center">
                <span class="rounded-full border min-w-7 h-7 flex items-center justify-center border-valence-grey-600" :class="[bgColorClass]">
                    <i :class="['text-valence-grey-600 bi text-[15px]/none', iconClass]" aria-hidden="true"></i>
                </span>
                <p class="body-small font-normal">{{ content }}</p>
            </div>
        </div>
        <button type="button" aria-label="preview role play report" class="rounded-full border border-valence-grey-300 w-8 h-8" @click="isPreviewSummaryOpen = true">
            <i class="bi bi-arrows-angle-expand icon-semibold text-sm leading-none" aria-hidden="true"></i>
        </button>
    </div>
    <BaseDialog v-model:open="isPreviewSummaryOpen" title="Sample role play recap">
        <div class="md:w-[calc(100dvw-80px)] max-w-250 p-6 pt-0 relative">
            <OverallSummary
                title="This is a sample role play recap"
                subtitle="Here's an example of highlights, insights and feedback"
                hide-cta
                :overall-summary="previewSummary"
                :summary-state="summaryState"
                :strengths="strengths"
                :weaknesses="weaknesses"
                :skills-breakdown="skillsBreakdown"
                :role-play-messages="rolePlayMessages" />
        </div>
    </BaseDialog>
</template>

<script setup>
import { ACTION } from "~vue/chatActions";
import { STATE } from "~vue/pages/RolePlaySummary.vue";
import OverallSummary from "~vue/RolePlaySummary/OverallSummary.vue";
import { ref } from "vue";

import BaseDialog from "./BaseDialog.vue";
const isPreviewSummaryOpen = ref(false);

const previewContent = [
    { iconClass: "bi-journal-text", content: "Performance Breakdown", bgColorClass: "bg-[#F1E4F7]" },
    { iconClass: "bi-lightbulb", content: "Personalized Insights", bgColorClass: "bg-[#FFE4EC]" },
    { iconClass: "bi-signpost-split", content: "Actionable Tips", bgColorClass: "bg-[#FFF0E5]" },
];

const previewSummary =
    "You effectively communicated your concerns about micromanagement and demonstrated a willingness to seek constructive solutions. The role-play helped you practice clarifying expectations and respectful boundary-setting, though deeper engagement could further enhance your confidence in navigating challenging conversations.";
const strengths = [
    "**Raised concerns constructively**: You clearly articulated your concerns about micromanagement and provided specific examples of the behaviors that were challenging for you.",
    "**Open to collaborative solutions**: You agreed to work together to clarify expectations and showed willingness to role-play and practice approaches for addressing the issue.",
    "**Respectful communication**: You proposed a diplomatic and solution-focused way to open the conversation with Patrick, aiming to improve meeting productivity.",
];
const weaknesses = [
    "**Hesitation to engage deeply**: You sometimes gave brief or repetitive responses (e.g., 'That sounds good', 'Hello', 'That's fine'), which limited deeper exploration of the issue.",
    "**Missed opportunities for follow-up**: When discussing Patrick's ambiguous responses, you could have practiced or explored more strategies for handling ambiguity in real time.",
];
const skillsBreakdown = [
    {
        name: "Communication Skills",
        score: 4,
        rationale:
            "You expressed your concerns about micromanagement clearly and articulated your desire to clarify expectations with Patrick, though at times your responses were brief or repeated, which slightly limited the depth of the conversation.",
        how_to_improve: "Aim to elaborate more on your thoughts and provide fuller responses to encourage a richer dialogue and deeper understanding.",
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: "Emotional Intelligence",
        score: 4,
        rationale:
            "You demonstrated self-awareness by identifying your feelings of frustration and hesitation, and you remained composed and respectful throughout the conversation.",
        how_to_improve: "You could further enhance empathy by acknowledging Patrick's possible perspective or motivations during the role-play.",
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: "Conflict Management",
        score: 5,
        rationale:
            "You chose a collaborative approach by seeking to clarify expectations and proposing constructive questions, but you expressed some hesitation about addressing ambiguity directly with Patrick.",
        how_to_improve: "Practice preparing specific follow-up questions or statements to address ambiguity and guide the conversation toward concrete outcomes.",
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: "Interpersonal Skills",
        score: 4,
        rationale: "You maintained professionalism and respect, and your approach to opening the conversation with Patrick was assertive yet considerate.",
        how_to_improve: "Continue to build rapport by expressing appreciation for the other person's time or contributions, even when addressing concerns.",
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: "Outcome",
        score: 3,
        rationale:
            "You achieved your goal of preparing to clarify expectations with Patrick and practiced framing the conversation, though the role-play could have benefited from exploring the scenario further.",
        how_to_improve: "Consider extending the role-play to practice handling potential pushback or ambiguous responses from Patrick.",
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: "Overall Effectiveness",
        score: 4,
        rationale:
            "You effectively identified the core issue, practiced a constructive approach, and demonstrated readiness to address the situation, though there is room to deepen the conversation and anticipate challenges.",
        how_to_improve: "Engage more deeply in the role-play to rehearse handling complex responses and to build greater confidence for the real conversation.",
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
];

const rolePlayMessages = [
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "Hi Jessy, it's great to catch up with you. I understand you have some concerns about how our meetings are being managed. I'd love to hear more about your perspective and how we can make them more effective for you.",
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: "So, I actually wanted to talk about my meetings with Patrick, so your manager, because I feel like he's been micromanaging me lately." },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: "You're taking initiative by bringing up a difficult topic directly and honestly, which is a strong communication skill.",
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "I appreciate you bringing this up, Jessy. I want to ensure you feel supported and heard. Can you share more about the specific behaviors or situations that have felt like micromanaging to you? Your feedback will help me understand the impact and find ways to improve our collaboration.",
            },
        ],
    },
    {
        role: "user",
        lines: [
            {
                type: "md",
                content:
                    "Yeah, so I feel like recently when I go to him for status updates that I've already shared with you, he will give me conflicting advice or criticisms that are sort of like outside the scope of our working relationship.",
            },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "suggestion",
                    feedback:
                        "Try to provide specific examples of the conflicting advice or criticisms you've received. This can help clarify the issue and make it easier to address in the conversation.",
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "It sounds frustrating to receive conflicting advice and feedback that doesn't align with our established process. Let's work together to find a way to address this constructively with Patrick. How about we discuss framing a conversation with him to clarify expectations and ensure alignment?",
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: "Yeah, that sounds good." },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: "You're open to collaborating and moving the conversation forward, which is a positive and constructive approach.",
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "Hi Jessy! It's great to see your engagement. Let's continue our role-play. How would you like to approach the conversation with Patrick? We can think about setting clear boundaries or seeking clarification on his expectations to improve the dynamic.",
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: "Sorry, could you repeat that?" }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "No problem! In our role-play, let's focus on how you'd like to address the conversation with Patrick. We can explore setting boundaries, clarifying expectations, or finding ways to align better with his management style. How would you like to proceed with that?",
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: "Let's start with clarifying." },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: "You're taking a proactive step by choosing to clarify expectations, which can help resolve misunderstandings and improve communication.",
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "Great choice! Clarifying expectations can help create a more productive dynamic. You could start by asking Patrick for more clarity on his feedback and the scope of your working relationship. Would you like to role-play how to approach this conversation with him?",
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: "Yes." }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "Alright, let's dive into the role-play. Imagine you're in a meeting with Patrick. How would you like to open the conversation to clarify his expectations?",
            },
        ],
    },
    {
        role: "user",
        lines: [
            {
                type: "md",
                content:
                    "Probably along the lines of like, hi Patrick, I feel like our meetings recently are just covering a lot of information I've already shared with Kai. So I'm wondering how we can make our time more productive for both of us, or how you want to set up these meetings.",
            },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: "You're expressing your concerns respectfully and suggesting improvements, which demonstrates constructive communication skills.",
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "That's a respectful and constructive approach. Patrick might appreciate your initiative to optimize your meetings. How do you think he might respond, and how would you like to continue the conversation?",
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: "I feel like he tends to respond ambiguously, which is why I haven't been able to have this conversation with him yet." },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "suggestion",
                    feedback: "Consider preparing follow-up questions to address ambiguity. This can help guide the conversation toward clearer outcomes.",
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    'Got it. In that case, it might help to gently guide the conversation by asking specific questions. For example, you could ask, "What specific outcomes are you looking for in our meetings that aren\'t being met currently?" This can encourage him to provide more concrete feedback. How does that sound to you?',
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: "Sounds good." }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "Great! Practice framing your questions to encourage clearer communication, and remember, I'm here to support you as you prepare. Shall we role-play a bit more, or is there anything else you'd like to address?",
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: "That's fine. That's all." }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content:
                    "Alright, Jessy. I'm glad we could work through this together. Feel free to reach out anytime you need further support or want to practice more. Take care, and best of luck with your conversation with Patrick!",
            },
        ],
    },
];
const summaryState = {
    overallSummary: STATE.LOADED,
    strengths: STATE.LOADED,
    weaknesses: STATE.LOADED,
};
</script>
